import {
  PatientListConfigurationListDto,
  QueryFactory,
} from '@lmt/shared-types';

import { Button } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import styles from '@lmt/unicorn-shared/app/components/patients/list/configuration/PatientListConfigurations.module.scss';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import PinIcon from '@lmt/unicorn-shared/app/icons/pin.svg?react';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface Props {
  currentConfigurationId?: number;
  onConfigurationChange: (newConfigurationId: number) => void;
  configurations?: PatientListConfigurationListDto[];
}

export const PatientListConfigurations: React.FC<Props> = React.memo(
  function PatientListConfigurationList(props) {
    const claims = useClaims();
    const i18nGlobal = useTranslation();
    const i18n = useScopedTranslation('patient_list_configuration');
    const forms = useGlobalForms();
    const modals = useModal();
    const queryClient = useQueryClient();
    const { onConfigurationChange, configurations, currentConfigurationId } =
      props;
    // this is used to close the popup and reset
    const [key, setKey] = useState(1);
    const closeDropdown = useCallback(() => {
      setKey((key) => ++key);
    }, []);

    const pinConfiguration = useCallback(
      async (configurationId: number, isPinned: boolean) => {
        try {
          await QueryFactory.PatientListConfigurationClient.pinPatientListConfiguration(
            configurationId,
            { isPinned: isPinned },
          );
          await queryClient.invalidateQueries({
            queryKey:
              QueryFactory.PatientListConfigurationQuery.getPatientListConfigurationsQueryKey(),
          });
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        }
      },
      [],
    );

    const renderConfigurationOption = useCallback(
      (config: PatientListConfigurationListDto) => {
        const canEdit =
          claims.permissions.manageSharedPatientListConfigurations ||
          claims.id === config.userId;
        return (
          <div className={styles.controls}>
            {canEdit && (
              <a
                className={styles.editLink}
                onClick={(event) => {
                  forms.openPatientListConfigurationEditing(config.id);
                  closeDropdown();
                  event.stopPropagation(); // to prevent onOptionClick
                }}
              >
                {i18n.t('list.edit_button' as any)}
              </a>
            )}
            <PinIcon
              className={styles.pin}
              data-is-pinned={config.isPinned}
              onClick={async (event) => {
                event.stopPropagation();
                await pinConfiguration(config.id, !config.isPinned);
              }}
            />
          </div>
        );
      },
      [pinConfiguration],
    );

    const renderDropdownBottom = useMemo(() => {
      return (
        <div
          className={styles.bottomContainer}
          onMouseDown={(e) => e.preventDefault()}
          data-not-empty={!!configurations?.length}
        >
          <Button
            testId={'add-new-configuration'}
            className={styles.addButton}
            title={i18n.t('list.add_button' as any) as string}
            onClick={() => {
              forms.openPatientListConfigurationCreating(undefined, (id) => {
                props.onConfigurationChange(id);
              });
              closeDropdown();
            }}
          />
        </div>
      );
    }, [closeDropdown]);

    const options = useMemo(() => {
      if (!configurations) {
        return [];
      }
      return configurations
        .map((configuration) => {
          const displayValue: string = i18nGlobal.t(configuration.name, {
            defaultValue: configuration.name,
          });
          return {
            ...configuration,
            displayValue: displayValue.toLowerCase(),
          };
        })
        .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
    }, [configurations]);

    return (
      <DropDownInput
        key={key}
        options={options}
        classes={{
          option: styles.option,
        }}
        rootClassName={styles.container}
        onValueChanged={(option) => onConfigurationChange(option!.id)}
        idFunction={'id'}
        getOptionLabel={(x) => i18nGlobal.t(x.name, { defaultValue: x.name })}
        required={true}
        postfixRenderer={renderConfigurationOption}
        useIdFunctionAsValue={true}
        value={currentConfigurationId}
        popupFooter={renderDropdownBottom}
        testId={'patient-list-configuration-list-control'}
      />
    );
  },
);
